import axios from 'axios';
import { APP_CONFIG } from '../config/appConfig';
import type { TryOnRequest, TryOnResult } from '../utils/tryon';
import type { PlaygroundTrace } from '../utils/toolCalls';
import type { RoleWithPermissions, SchemaDescriptor, RolePermissions } from '../types/rbac';
import type { RenderAs, CardMapping, CardMappingSource, InferCardMappingResponse } from '../types/connectorCards';
import type {
  OrchestratorActionConfirmResponse,
  OrchestratorCapabilitiesResponse,
  OrchestratorRunDetail,
  OrchestratorRunsResponse,
} from '../types/orchestrator';
import type {
  AuthResponse,
  User,
  Agent,
  AgentConfig,
  KnowledgeSource,
  Conversation,
  Ticket,
  TicketStats,
  TicketAssignee,
  AttachmentRef,
  PublicTicket,
  CommentMention,
  DashboardStats,
  ConversationActivity,
  TopIssue,
  SentimentData,
  WidgetConfig,
  CompanyUser,
  Role,
  Notification as AppNotification,
  EmailThread,
  EmailMessage,
  HelpCenterCollection,
  HelpCenterSettings as HCSettings,
  ConversationComment,
  ChangelogEntry,
  PendingInvitation,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Custom error class for 429 Rate Limit responses
export class RateLimitError extends Error {
  retryAfterSeconds: number;

  constructor(message: string, retryAfterSeconds: number) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// Global event for 429 errors — components can listen to show toasts as a fallback
export const RATE_LIMIT_EVENT = 'api:rate-limit';

// Global event for 503 maintenance responses — MaintenanceContext listens
// and force-flips to ongoing without waiting for the next SSE/poll tick.
// Plan-MaintenanceBanners.md §E.1 step 5.
export const MAINTENANCE_DETECTED_EVENT = 'maintenance:detected';

// `GET /api/widget/config/{agent_id}` — the widget's first, blocking request.
// It is unauthenticated and cacheable, so it is the ONE call worth keeping as a
// CORS-"simple" request: any header outside the safelist (Content-Type other
// than the three form types, Cache-Control, Pragma, X-Domain-Mode, …) forces a
// preflight OPTIONS round trip onto the critical path.
const WIDGET_CONFIG_RE = /^\/api\/widget\/config\/[^/]+$/;

const api = axios.create({
  baseURL: API_URL,
  // Content-Type is declared PER METHOD rather than globally. As a global
  // default it was also sent on GET/HEAD, which are bodyless — the header is
  // meaningless there and, being outside the CORS safelist, forced a preflight
  // on every cross-origin GET. Bodied verbs keep the exact header they had, so
  // the 11 `multipart/form-data` upload call sites and any raw-string body are
  // byte-identical to before. (axios would infer `application/json` for a plain
  // object anyway, but NOT for a string — it would pick
  // `application/x-www-form-urlencoded` — so these buckets are load-bearing.)
  headers: {
    post: { 'Content-Type': 'application/json' },
    put: { 'Content-Type': 'application/json' },
    patch: { 'Content-Type': 'application/json' },
    delete: { 'Content-Type': 'application/json' },
  },
  // Honor Set-Cookie responses from the backend (e.g. the auto-issued
  // `prodagi_session` cookie used to gate `/admin-help-center/*`). The
  // browser silently drops cross-origin cookies unless credentials are
  // included on the XHR. Bearer-token auth via the request interceptor
  // below still works — `withCredentials` is additive, not a swap.
  withCredentials: true,
});

// Request interceptor to add auth token and active workspace
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const workspaceId = localStorage.getItem('active_workspace_id');
  if (workspaceId) {
    config.headers['X-Workspace-ID'] = workspaceId;
  }

  // Plan-ShopifyIsolatedFrontend.md §2 + §3.1 — the backend uses
  // `X-Domain-Mode` to partition workspaces by billing provider on
  // the workspace-switcher endpoint. The Stripe build deliberately
  // sends NO header (Storm's spec treats absent as `stripe` by
  // default); only the Shopify build advertises itself.
  // The widget config GET must stay CORS-"simple" — see WIDGET_CONFIG_RE. It is
  // fetched before the panel can render, so a preflight here is pure latency.
  const isWidgetConfig = config.method === 'get' && WIDGET_CONFIG_RE.test(config.url || '');

  if (APP_CONFIG.billingProvider === 'shopify' && !isWidgetConfig) {
    config.headers['X-Domain-Mode'] = 'shopify';
  }

  // `no-cache` on every GET actively defeats any `Cache-Control` the server
  // sends: the browser revalidates regardless. Keep it everywhere it has always
  // been (dashboard reads must not go stale — most of those endpoints send no
  // cache headers at all, so the browser would otherwise heuristically cache
  // them), and drop it for the one endpoint that is explicitly cacheable.
  // `version.json` is fetched outside this client, by VersionUpdateManager.
  if (config.method === 'get' && !isWidgetConfig) {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Scope the auto-logout: a 401 from a widget-scoped endpoint
      // (`/api/widget/...` — chat, history, playground comments, the
      // conversation-detail resume) is an AUTHORIZATION result for that
      // widget/playground action ("not playground staff for this agent"),
      // NOT a dashboard session-expiry. Widget endpoints authenticate via
      // the `prodagi_session` cookie, not the dashboard bearer token, so
      // their 401 must not nuke the dashboard session / redirect to '/'.
      // Let those callers surface the error locally (e.g. the playground
      // comment toast). Genuine session-expiry on real dashboard/auth
      // endpoints still logs out exactly as before — interceptor scope is
      // narrowed by URL only, nothing else changed.
      const reqUrl: string = error.config?.url || '';
      const isWidgetScoped = reqUrl.includes('/api/widget/');
      if (!isWidgetScoped) {
        localStorage.removeItem('token');
        window.location.href = '/';
      }
    }

    // 503 with `reason=maintenance_ongoing` body → broadcast a
    // CustomEvent so MaintenanceContext can flip to ongoing immediately
    // (without waiting for the SSE / 30s poll). Plan-MaintenanceBanners.md
    // §E.1 step 5. We deliberately fire-and-forget here and let the
    // calling code still see the rejected promise — UI toasts on top
    // of the maintenance modal would be noise, but components / pages
    // that handle the error themselves remain free to react.
    if (
      error.response?.status === 503 &&
      error.response?.data?.reason === 'maintenance_ongoing'
    ) {
      try {
        window.dispatchEvent(
          new CustomEvent(MAINTENANCE_DETECTED_EVENT, {
            detail: error.response.data,
          }),
        );
      } catch {
        // Defensive — never let the interceptor itself throw.
      }

      // Normalize the response body so downstream pages that do
      // `showToast(err.response?.data?.detail || '…', 'error')` cannot
      // accidentally render the maintenance OBJECT as a React child
      // (which trips React error #31 and unmounts the page tree, leaving
      // the user staring at a blank page underneath the maintenance
      // blocker modal). Dispatch above happens FIRST so MaintenanceContext
      // still sees the full original payload; we only mutate the copy
      // that the rejected promise carries downstream.
      try {
        const mt = error.response?.data?.message_title;
        const safeDetail =
          typeof mt === 'string' && mt
            ? mt
            : 'Service temporarily unavailable for maintenance.';
        error.response.data = {
          detail: safeDetail,
          reason: 'maintenance_ongoing',
        };
      } catch {
        // Defensive — never let the interceptor itself throw.
      }
    }

    // Transform 429 into a typed RateLimitError
    if (error.response?.status === 429) {
      const retryAfter =
        error.response.data?.retry_after_seconds ??
        (parseInt(error.response.headers?.['retry-after'] || '0', 10) || 45);
      const message =
        error.response.data?.message || 'Too many requests. Please slow down.';
      window.dispatchEvent(
        new CustomEvent(RATE_LIMIT_EVENT, { detail: { message, retryAfterSeconds: retryAfter } })
      );
      return Promise.reject(new RateLimitError(message, retryAfter));
    }

    return Promise.reject(error);
  }
);

// Authentication
export const authAPI = {
  register: (data: { email: string; password: string; full_name: string; company_name?: string; recaptcha_token?: string; first_name?: string; last_name?: string; trial_token?: string }) =>
    api.post<AuthResponse>('/api/auth/register', data),

  // Plan-TrialLinkInvalidTokenAndEnvHosts.md — UX gate for the global trial
  // signup link. Boolean oracle on the single already-public token; the backend
  // stays authoritative for the actual grant. Response `{ valid: boolean }`,
  // sent with `Cache-Control: no-store` so a deactivated link isn't cached valid.
  validateTrialToken: (token: string) =>
    api.get<{ valid: boolean }>(`/api/auth/trial-signup-link/validate?token=${encodeURIComponent(token)}`),

  login: (email: string, password: string, recaptcha_token?: string) => {
    const url = recaptcha_token
      ? `/api/auth/login?recaptcha_token=${encodeURIComponent(recaptcha_token)}`
      : '/api/auth/login';
    return api.post<AuthResponse>(url, new URLSearchParams({ username: email, password }), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },

  googleAuth: (data: { google_token: string; full_name: string; email: string; google_id: string; profile_picture?: string; first_name?: string; last_name?: string; trial_token?: string }) =>
    api.post<AuthResponse>('/api/auth/google', data),

  // Shopify OAuth (Shopify build only — see Plan-ShopifySignInLogin.md).
  // Step 1: ask the backend to build the per-shop authorize URL. The FE then
  // redirects the browser to `authorize_url`.
  shopifyAuthUrl: (shop: string, redirect_uri: string, intent: 'login' | 'signup', trial_token?: string) =>
    api.post<{ authorize_url: string }>('/api/auth/shopify/url', { shop, redirect_uri, intent, trial_token }),

  // Step 2: Shopify redirects back to /auth/shopify/callback with the OAuth
  // params; exchange them for our session. Response mirrors /api/auth/google.
  shopifyAuth: (data: { code: string; shop: string; hmac: string; state: string; redirect_uri: string; [key: string]: string }) =>
    api.post<AuthResponse>('/api/auth/shopify', data),

  getCurrentUser: () => api.get<User>('/api/auth/me'),

  logout: () => api.post('/api/auth/logout'),

  forgotPassword: (email: string) =>
    api.post('/api/auth/forgot-password', { email }),

  resetPassword: (token: string, new_password: string) =>
    api.post('/api/auth/reset-password', { token, new_password }),
};

// AI Agents
export const agentsAPI = {
  list: () => api.get<Agent[]>('/api/agents/'),

  create: (config: AgentConfig) => api.post<Agent>('/api/agents/', { config }),

  get: (agentId: string) => api.get<Agent>(`/api/agents/${agentId}`),

  update: (agentId: string, data: { config?: AgentConfig; is_active?: boolean; is_published?: boolean; help_center_slug?: string; files?: { [key: string]: Blob | null } }) => {
    const formData = new FormData();

    if (data.config) formData.append('config', JSON.stringify(data.config));
    if (data.is_active !== undefined) formData.append('is_active', String(data.is_active));
    if (data.is_published !== undefined) formData.append('is_published', String(data.is_published));
    // 🚨 TOP-LEVEL form field, a sibling of `config` — NOT a key inside the
    // `config` JSON blob. `AgentConfig` has no such field, so nesting it would
    // be silently dropped server-side and the save would appear to succeed
    // while changing nothing.
    if (data.help_center_slug !== undefined) formData.append('help_center_slug', data.help_center_slug);

    if (data.files) {
      Object.entries(data.files).forEach(([purpose, blob]) => {
        if (blob) {
          const file = new File([blob], `${purpose}.png`, { type: 'image/png' });
          formData.append(purpose, file);
        }
      });
    }

    return api.put<Agent>(`/api/agents/${agentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  delete: (agentId: string) => api.delete(`/api/agents/${agentId}`),

  getEmbedCode: (agentId: string) =>
    // Plan-ComponentEmbed (2026-05-18): `component_embed_enabled` is the
    // EmbedCodeModal gate — verified vs SHIPPED back/api/agents.py:685
    // (get_embed_code return) @ e8c8d5a. Read-only mirror of the
    // top-level `agents.component_embed_enabled` Mongo field.
    api.get<{ agent_id: string; embed_code: string; instructions: string; component_embed_enabled?: boolean }>(`/api/agents/${agentId}/embed-code`),

  getIndustryTemplates: () =>
    api.get<{ templates: { id: string; title: string; instruction: string }[] }>('/api/agents/templates/list'),

  uploadImage: (file: File, agentId?: string, purpose?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (agentId) formData.append('agent_id', agentId);
    if (purpose) formData.append('purpose', purpose);

    return api.post<{ url: string; filename: string; size: number; agent_id?: string; purpose?: string }>('/api/agents/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  bulkUploadImages: (agentId: string, files: { [key: string]: Blob | null }) => {
    const formData = new FormData();
    formData.append('agent_id', agentId);

    Object.entries(files).forEach(([purpose, blob]) => {
      if (blob) {
        const file = new File([blob], `${purpose}.png`, { type: 'image/png' });
        formData.append(purpose, file);
      }
    });

    return api.post<{ status: string; agent_id: string; uploads: { [key: string]: string } }>('/api/agents/bulk-upload-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Knowledge Hub
export const knowledgeAPI = {
  createManual: (
    agentId: string,
    title: string,
    content: string,
    status: 'draft' | 'published' = 'published',
    description: string = '',
    aiGenerateDescription: boolean = false,
  ) =>
    api.post<KnowledgeSource>('/api/knowledge/manual', {
      agent_id: agentId,
      title,
      content,
      status,
      description,
      ai_generate_description: aiGenerateDescription,
    }),

  uploadFiles: (agentId: string, files: File[], metadata: { title: string; description: string; status: 'draft' | 'published'; skip_summary?: boolean }[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('metadata', JSON.stringify(metadata));
    return api.post<KnowledgeSource[]>(`/api/knowledge/upload?agent_id=${agentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  scrapeUrls: (agentId: string, urls: string[], status: 'draft' | 'published' = 'published') =>
    api.post<KnowledgeSource[]>('/api/knowledge/web-scrape', { agent_id: agentId, urls, status }),

  googleSearch: (agentId: string, query: string, numResults: number = 5) =>
    api.post<KnowledgeSource[]>('/api/knowledge/google-search', {
      agent_id: agentId,
      query,
      num_results: numResults,
    }),

  list: (agentId: string) => api.get<KnowledgeSource[]>(`/api/knowledge/${agentId}`),

  get: (knowledgeId: string) => api.get<KnowledgeSource>(`/api/knowledge/article/${knowledgeId}`),

  update: (
    knowledgeId: string,
    title: string,
    content: string,
    status?: 'draft' | 'published',
    description?: string,
    aiGenerateDescription?: boolean,
  ) =>
    api.put<KnowledgeSource>(`/api/knowledge/${knowledgeId}`, {
      title,
      content,
      ...(status && { status }),
      ...(description !== undefined && { description }),
      ...(aiGenerateDescription !== undefined && { ai_generate_description: aiGenerateDescription }),
    }),

  delete: (knowledgeId: string) => api.delete(`/api/knowledge/${knowledgeId}`),

  bulkAction: (ids: string[], action: 'publish' | 'draft' | 'delete') =>
    api.patch<{ affected: number }>('/api/knowledge/bulk', { ids, action }),

  bulkHelpCenter: (ids: string[], enabled: boolean) =>
    api.patch<{ affected: number }>('/api/knowledge/bulk-help-center', { ids, enabled }),

  bulkCollection: (ids: string[], collection_id: string | null) =>
    api.patch<{ affected: number }>('/api/knowledge/bulk-collection', { ids, collection_id }),

  bulkCollectionTransfer: (data: {
    collection_ids: string[];
    source_agent_id: string;
    target_agent_id: string;
    action: 'move' | 'copy';
    include_articles?: boolean;
  }) =>
    api.post<{ action: 'move' | 'copy'; transferred: number; collections: number; target_agent_id: string }>(
      '/api/knowledge/bulk-collection-transfer',
      data,
    ),

  uploadImage: (agentId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ url: string; filename: string; size: number }>(`/api/knowledge/upload-image?agent_id=${agentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getPreview: (docId: string) =>
    api.get<{
      preview_type: 'markdown' | 'text' | 'table';
      content: string | null;
      preview_data: { columns: string[]; rows: string[][] } | null;
    }>(`/api/knowledge/${docId}/preview`),

  analytics: (agentId: string) =>
    api.get<{
      articles: { article_id: string; title: string; view_count: number; helpful_count: number; not_helpful_count: number; helpfulness_pct: number }[];
      totals: { total_views: number; total_helpful: number; total_not_helpful: number; overall_helpfulness_pct: number };
      top_viewed: { article_id: string; title: string; view_count: number; helpful_count: number; not_helpful_count: number; helpfulness_pct: number }[];
      worst_rated: { article_id: string; title: string; view_count: number; helpful_count: number; not_helpful_count: number; helpfulness_pct: number }[];
    }>(`/api/knowledge/analytics?agent_id=${agentId}`),

  toggleHelpCenter: (knowledgeId: string, enabled: boolean) =>
    api.patch(`/api/knowledge/${knowledgeId}/help-center`, { enabled }),

  updateHelpCenterMeta: (knowledgeId: string, data: {
    enabled?: boolean;
    order?: number;
    meta_title?: string;
    meta_description?: string;
    help_center_slug?: string;
    collection_id?: string | null;
  }) => api.patch(`/api/knowledge/${knowledgeId}/help-center`, data),

  transfer: (knowledgeId: string, targetAgentId: string, action: 'move' | 'copy') =>
    api.post<{ success: boolean; action: 'move' | 'copy'; knowledge_id: string }>(
      `/api/knowledge/${knowledgeId}/transfer`,
      { target_agent_id: targetAgentId, action }
    ),

  bulkTransfer: (
    ids: string[],
    targetAgentId: string,
    action: 'move' | 'copy'
  ) =>
    api.post<{
      success: boolean;
      action: 'move' | 'copy';
      moved: number;
      copied: number;
      failed: number;
    }>(`/api/knowledge/bulk-transfer`, {
      knowledge_ids: ids,
      target_agent_id: targetAgentId,
      action,
    }),

  cleanupPendingMedia: (s3Keys: string[]) =>
    api.post<{ success: boolean; deleted: number; skipped: number; errors: number }>(
      '/api/knowledge/pending-media/cleanup',
      { s3_keys: s3Keys }
    ),

  uploadVideo: (agentId: string, file: File, onUploadProgress?: (progressEvent: any) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ url: string; filename: string; size: number }>(`/api/knowledge/upload-video?agent_id=${agentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress,
    });
  },
};

// Playground
export const playgroundAPI = {
  // NOTE: `chat` (POST /api/playground/chat) and `getHistory`
  // (GET /api/playground/history/{session_id}) were removed 2026-05-18
  // (task #9) — zero callers; their only consumer (CostTest.tsx) was
  // deleted. Backend routes retired separately by Storm. Live endpoints
  // kept: uploadAttachment (Inbox ChatsTab) + deleteHistory (Playground).
  uploadAttachment: (agentId: string, sessionId: string, file: File) => {
    const formData = new FormData();
    formData.append('agent_id', agentId);
    formData.append('session_id', sessionId);
    formData.append('file', file);
    return api.post<{ s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string }>(
      '/api/playground/upload-attachment',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  deleteHistory: (sessionIds: string[]) => api.post('/api/playground/history/delete', { session_ids: sessionIds }),
};

// Dashboard
function dashboardParams(days: number, agentIds?: string[]): string {
  const params = new URLSearchParams({ days: String(days) });
  if (agentIds?.length) params.set('agent_ids', agentIds.join(','));
  return params.toString();
}

export const dashboardAPI = {
  getStats: (days: number = 30, agentIds?: string[]) =>
    api.get<DashboardStats>(`/api/dashboard/stats?${dashboardParams(days, agentIds)}`),

  getConversationActivity: (days: number = 7, agentIds?: string[]) =>
    api.get<ConversationActivity[]>(`/api/dashboard/conversations?${dashboardParams(days, agentIds)}`),

  getTopIssues: (limit: number = 5) => api.get<TopIssue[]>(`/api/dashboard/top-issues?limit=${limit}`),

  getSentiment: () => api.get<SentimentData>('/api/dashboard/sentiment'),

  getRecentActivity: (limit: number = 10) =>
    api.get<any[]>(`/api/dashboard/recent-activity?limit=${limit}`),

  getCsatMetrics: (days: number = 30, agentIds?: string[]) =>
    api.get<{ distribution: Record<number, number>; average: number | null; total: number; surveys_sent: number; surveys_returned: number; survey_return_rate: number }>(`/api/dashboard/csat-metrics?${dashboardParams(days, agentIds)}`),

  getEscalationBreakdown: (days: number = 30, agentIds?: string[]) =>
    api.get<{ reason: string; count: number }[]>(`/api/dashboard/escalation-breakdown?${dashboardParams(days, agentIds)}`),

  getResolutionTime: (days: number = 30, agentIds?: string[]) =>
    api.get<{ avg_resolution_time_ms: number; trend: { week: string; avg_ms: number; count: number }[] }>(`/api/dashboard/resolution-time?${dashboardParams(days, agentIds)}`),

  getChannelSplit: (days: number = 30, agentIds?: string[]) =>
    api.get<{ chat: { total: number; resolved: number; resolution_rate: number }; email: { total: number; resolved: number; resolution_rate: number } }>(`/api/dashboard/channel-split?${dashboardParams(days, agentIds)}`),

  // Deflection outcome split (2026-07-23) — `deflection_rate` is RESOLVED-ONLY.
  // It used to fold in conversations a Celery staleness sweep flipped to
  // `auto_resolved` after 3 silent days, i.e. customers who gave up. Those are
  // now their own bucket, `abandonment_rate`. The new field is optional here
  // because the backend ships separately: pre-deploy it is simply absent and
  // the Dashboard normalises it to 0 on ingest.
  getAgentComparison: (days: number = 30, agentIds?: string[]) =>
    api.get<{ agent_id: string; agent_name: string; total: number; deflection_rate: number; escalation_rate: number; abandonment_rate?: number; avg_csat: number }[]>(`/api/dashboard/agent-comparison?${dashboardParams(days, agentIds)}`),

  // Same split, weekly. `deflected`/`rate` are redefined to resolved-only; the
  // four bucket counts (resolved + escalated + abandoned + unknown) are
  // mutually exclusive and sum to `total`. Optional for the same staged-deploy
  // reason as above.
  getDeflectionTrend: (days: number = 30, agentIds?: string[]) =>
    api.get<{ week: string; total: number; deflected: number; rate: number; resolved?: number; escalated?: number; abandoned?: number; unknown?: number }[]>(`/api/dashboard/deflection-trend?${dashboardParams(days, agentIds)}`),

  getCsatTrend: (days: number = 30, agentIds?: string[]) =>
    api.get<{ week: string; avg_csat: number; total_ratings: number }[]>(`/api/dashboard/csat-trend?${dashboardParams(days, agentIds)}`),
};

// Tickets
export const ticketsAPI = {
  list: (status?: string, priority?: string) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    return api.get<Ticket[]>(`/api/tickets/?${params.toString()}`);
  },

  get: (ticketId: string) => api.get<Ticket>(`/api/tickets/${ticketId}`),

  update: (ticketId: string, data: { status?: string; priority?: string; assigned_to?: string; category?: string }) =>
    api.put<Ticket>(`/api/tickets/${ticketId}`, data),

  // People who can be assigned tickets in the active workspace (owner, Admin,
  // Tickets Manager, or a custom role with full ticket CRUD). The dropdown used
  // to list every team member from usersAPI.list(), Viewers included.
  getAssignees: () => api.get<TicketAssignee[]>('/api/tickets/assignees'),

  // Plan-TicketComments-AudienceMentions — `is_internal` (audience) +
  // resolved `mentions`. Matches BE AddCommentRequest. Optional opts keep
  // existing callers (default: customer-facing, no mentions) compiling.
  addComment: (
    ticketId: string,
    text: string,
    opts?: { is_internal?: boolean; mentions?: CommentMention[]; attachments?: AttachmentRef[] },
  ) =>
    api.post<Ticket>(`/api/tickets/${ticketId}/comments`, {
      text,
      is_internal: opts?.is_internal ?? false,
      mentions: opts?.mentions ?? [],
      // 2026-09-16: files belong to THIS comment — emailed to the customer on a
      // customer-facing reply, team-only on an internal note.
      attachments: opts?.attachments ?? [],
    }),

  // Upload one file for a comment and get its ref back. Unlike
  // uploadAttachment (below) this does NOT attach it to the ticket itself.
  uploadCommentAttachment: (ticketId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<AttachmentRef>(`/api/tickets/${ticketId}/comment-attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getStats: () => api.get<TicketStats>('/api/tickets/stats'),

  delete: (ticketId: string) => api.delete(`/api/tickets/${ticketId}`),

  uploadAttachment: (ticketId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<Ticket>(`/api/tickets/${ticketId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Public (no-auth) per-ticket status page — Plan-Phase1-TicketPortal.md.
// Reuses the shared `api` instance (the request interceptor only ADDS a
// bearer token when one happens to exist; this endpoint ignores it). The
// response interceptor auto-logs-out on 401 ONLY, and neither endpoint
// here ever returns 401 — an invalid/unknown token is a 404 — so an
// anonymous visitor's failed lookup or reply never nukes a session.
// Mirrors the changelogAPI anon pattern.
//
// `addComment` is reply-by-web (added 2026-09-15): the customer's reply
// lands as the same comment shape reply-by-email writes. 200 → the full
// refreshed PublicTicket (status may have flipped back to `open` when a
// resolved/closed ticket is replied to); 404 unknown token; 422 / 429
// carry a human-readable `detail` string for inline display.
export const publicTicketsAPI = {
  get: (token: string) =>
    api.get<PublicTicket>(`/api/public/tickets/${encodeURIComponent(token)}`),
  addComment: (token: string, text: string, attachments: AttachmentRef[] = []) =>
    api.post<PublicTicket>(`/api/public/tickets/${encodeURIComponent(token)}/comments`, {
      text,
      attachments,
    }),
  // Customer-side upload for a reply: images or PDF, 10MB each, 3 per reply.
  // The ref must come from here — the reply only accepts files uploaded for
  // that same ticket.
  uploadAttachment: (token: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<AttachmentRef>(
      `/api/public/tickets/${encodeURIComponent(token)}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
};

// Chats
export const chatsAPI = {
  list: (agentId?: string, dateFrom?: string, dateTo?: string, source?: string) =>
    api.get<Conversation[]>('/api/chats/', {
      params: {
        agent_id: agentId || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        source: source || undefined,
      },
    }),
  get: (sessionId: string) => api.get<Conversation>(`/api/chats/${sessionId}`),
  /**
   * STAFF tool-call audit for ANY conversation the caller's org owns
   * (Plan-DecoupledToolCallAudit.md). Authorized by the BEARER token
   * (auto-attached by the request interceptor) + agent ownership — NOT the
   * `source:"playground"` cookie stamp, which SameSite=Lax withholds on the
   * staging cross-site split. So the Playground Tool-Calls panel populates on
   * staging AND prod, and for `source:None` conversations, where the old
   * `widgetAPI.getPlaygroundTrace` 404'd.
   *
   * Same response shape as the old trace endpoint (`PlaygroundTrace`), so the
   * panel binds with zero changes. Denial is still 404 (never 403) — but for an
   * owned session it essentially never fires, which is the point.
   */
  getConversationToolCalls: (sessionId: string) =>
    api.get<PlaygroundTrace>(`/api/chats/${sessionId}/tool-calls`),
  /**
   * STAFF Playground history for an agent, scoped by `visitorId`
   * (Plan-DecoupledToolCallAudit.md Slice 2). Bearer-authed (auto-attached) +
   * agent-ownership, SOURCE-AGNOSTIC — so it returns the staff's own test chats
   * on STAGING, where conversations are stored `source:None` and the old
   * `widgetAPI.getHistory({source:'playground'})` 401s cross-site (the staff
   * cookie is withheld). `visitorId` is the stable per-browser id every staff
   * playground chat shares (real customers have different ones); the Playground
   * parent reads it from the shared same-origin localStorage. Empty/missing
   * visitor_id → empty list.
   *
   * Same item shape as the old playground history (`widgetAPI.getHistory`), so
   * the Playground `sessions` binding is UNCHANGED.
   */
  getAgentPlaygroundHistory: (agentId: string, visitorId: string) =>
    api.get<{
      conversations: {
        session_id: string;
        created_at: string;
        message_count: number;
        preview: string;
        created_by_id?: string | null;
        created_by_name?: string | null;
        comments?: ConversationComment[];
      }[];
    }>(`/api/chats/agent/${agentId}/conversations`, {
      params: { visitor_id: visitorId },
    }),
  assign: (sessionId: string, assignedTo: string) =>
    api.put(`/api/chats/${sessionId}/assign`, { assigned_to: assignedTo }),
  reply: (sessionId: string, message: string, attachments?: Array<{ s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string }>) =>
    api.post(`/api/chats/${sessionId}/reply`, { message, attachments }),
  addComment: (sessionId: string, text: string) =>
    api.post(`/api/chats/${sessionId}/comments`, { text }),
  // Per Plan-HumanAgentHandoff-2026-06-09 §Backend endpoints — staff-only,
  // first-takeover-wins (409 on race), atomic Mongo update server-side.
  takeover: (sessionId: string) =>
    api.post<Conversation>(`/api/chats/${sessionId}/takeover`),
  releaseHandoff: (sessionId: string) =>
    api.post<Conversation>(`/api/chats/${sessionId}/release-handoff`),
  // Plan-HumanAgentHandoff-2026-06-12 — mint a 5-minute scoped JWT the
  // ChatsTab staff WebSocket can authenticate with. Browsers can't
  // attach Authorization headers to WS handshakes, so we forward the
  // token as a query string instead. Backend: chats.py:427.
  issueWsToken: () =>
    api.post<{ token: string; expires_at: string }>('/api/chats/ws-token'),
};

// RBAC custom roles — Plan-RBAC-SchemaRoles-2026-08.md §6.2.
//
// 🚨 Separate from `usersAPI.getRoles()`, which hits the LEGACY
// `/api/users/roles` and returns `{id, name, description}` with no permissions.
// That endpoint still backs the role PICKER; this one backs the role EDITOR.
// Do not merge them — the legacy picker must keep working with the custom-roles
// flag off.
export const rolesAPI = {
  /** Catalogue: presets (workspace_id null) + this workspace's custom roles. */
  list: () => api.get<RoleWithPermissions[]>('/api/roles'),

  /**
   * Matrix rows. 🚨 A schema whose feature flags are all off is OMITTED from
   * this response — absence IS the hiding. Never substitute a hardcoded list.
   */
  schemas: () => api.get<SchemaDescriptor[]>('/api/roles/schemas'),

  create: (body: { name: string; description?: string; permissions: RolePermissions }) =>
    api.post<RoleWithPermissions>('/api/roles', body),

  /** 403s on a `system: true` preset — the UI must not offer it. */
  update: (
    roleId: string,
    body: { name?: string; description?: string; permissions?: RolePermissions },
  ) => api.patch<RoleWithPermissions>(`/api/roles/${roleId}`, body),

  /** 403s on a `system: true` preset — the UI must not offer it. */
  remove: (roleId: string) => api.delete<void>(`/api/roles/${roleId}`),
};

// Team / Users Management
export const usersAPI = {
  list: () => api.get<CompanyUser[]>('/api/users/'),

  getRoles: () => api.get<Role[]>('/api/users/roles'),

  // NOTE: the legacy user-scoped `invite` / `remove` methods were removed —
  // team membership is workspace-scoped (workspaceAPI.invite / .removeMember)
  // and the matching `/api/users/invite` + `DELETE /api/users/{id}` backend
  // routes are deleted. Do not re-add without a live backend route.
  updateRoles: (userId: string, roles: string[]) =>
    api.put<CompanyUser>(`/api/users/${userId}/role`, { roles }),
  requestRole: (roles: string[]) =>
    api.post<CompanyUser>('/api/users/request-role', { roles }),
  approveRole: (userId: string) =>
    api.post<CompanyUser>(`/api/users/${userId}/approve-role`),
  rejectRole: (userId: string) =>
    api.post<CompanyUser>(`/api/users/${userId}/reject-role`),
};

// Changelog (public, anon-safe)
export const changelogAPI = {
  getActive: () =>
    api.get<{ entry: ChangelogEntry | null }>('/api/changelog/active'),
  list: (cursor?: string, limit: number = 20) => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    params.set('limit', String(limit));
    return api.get<{ entries: ChangelogEntry[]; next_cursor: string | null }>(
      `/api/changelog?${params.toString()}`,
    );
  },
  getBySlug: (slug: string) =>
    api.get<ChangelogEntry>(`/api/changelog/${encodeURIComponent(slug)}`),
};

// Notifications
export const notificationsAPI = {
  list: (unreadOnly?: boolean) => api.get<AppNotification[]>('/api/notifications/', { params: { unread_only: unreadOnly } }),
  getUnreadCount: () => api.get<{ count: number }>('/api/notifications/unread-count'),
  markAsRead: (id: string) => api.put<AppNotification>(`/api/notifications/${id}/read`),
  markAllAsRead: () => api.put('/api/notifications/read-all'),
};

// Emails
export const emailsAPI = {
  list: (agentId?: string) =>
    api.get<EmailThread[]>('/api/emails/', {
      params: {
        agent_id: agentId || undefined,
      },
    }),
  markAsRead: (threadId: string) => api.put<{ status: string, is_read: boolean }>(`/api/emails/${threadId}/read`),
  reply: (
    threadId: string,
    replyData: { text_body: string; html_body: string; attachments?: string[]; attachment_refs?: AttachmentRef[] },
  ) => api.post<EmailMessage>(`/api/emails/${threadId}/reply`, replyData),
  // Upload one file, then pass the returned ref on `attachment_refs`. Files
  // ride on the email itself up to ~7MB total; the rest become links.
  uploadAttachment: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<AttachmentRef>('/api/emails/attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  assign: (threadId: string, assignedTo: string) =>
    api.put<{ status: string; assigned_to: string; assigned_to_name: string }>(`/api/emails/${threadId}/assign`, { assigned_to: assignedTo }),
  addComment: (threadId: string, text: string) =>
    api.post(`/api/emails/${threadId}/comments`, { text }),
  generateReply: (threadId: string) =>
    api.post<{ reply: string }>(`/api/emails/${threadId}/generate-reply`),
  sendNew: (data: { agent_id: string; to_address: string; subject: string; text_body: string; html_body: string; attachments?: string[]; attachment_refs?: AttachmentRef[] }) =>
    api.post<EmailThread>('/api/emails/send-new', data),
  addAttachmentsToTicket: (threadId: string, ticketId: string, attachmentRefs: Array<{ s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string }>) =>
    api.post(`/api/emails/${threadId}/attachments/add-to-ticket`, {
      ticket_id: ticketId,
      attachment_refs: attachmentRefs,
    }),
};

// Voice / Avatar
export const voiceAPI = {
  createAvatarSession: () => api.post('/api/voice/avatar-session'),
};

// Shopify Integration
export interface ShopifyStore {
  id: string;
  shop_domain: string;
  shop_name: string;
  connector_count: number;
  installed_at: string;
  widget_installed: boolean;
  needs_reauth?: boolean;
  reauth_reason?: string | null;
}

export const shopifyAPI = {
  getStatusAll: () =>
    api.get<Record<string, ShopifyStore[]>>('/api/integrations/shopify/status/all'),
  getStatus: (agentId: string) =>
    api.get<{ stores: ShopifyStore[] }>('/api/integrations/shopify/status', { params: { agent_id: agentId } }),
  // Plan-AutoInstallCommerceAgent §4a (2026-05-26): for Shopify-billed
  // workspaces, the FE may omit `agent_id` — backend resolves via the
  // resolver ladder. Stripe-billed workspaces keep the legacy behavior
  // (agent_id required → 400 otherwise) since the resolver refuses them.
  getAuthUrl: async (shop: string, agentId?: string) => {
    const params = new URLSearchParams({ shop });
    if (agentId) params.set('agent_id', agentId);
    const res = await api.get<{ url: string }>(`/api/integrations/shopify/auth-url?${params}`);
    window.location.href = res.data.url;
  },
  disconnect: (integrationId: string) =>
    api.delete(`/api/integrations/shopify/disconnect/${integrationId}`),
  installWidget: (agentId: string, integrationId: string) =>
    api.post<{ message: string }>('/api/integrations/shopify/install-widget', { agent_id: agentId, integration_id: integrationId }),
  uninstallWidget: (integrationId: string) =>
    api.post<{ message: string }>('/api/integrations/shopify/uninstall-widget', { integration_id: integrationId }),
  // Plan-ShopifyDefaultArticles.md §4.4 + §5.3 (pivoted 2026-06-15) — manual
  // import of seeded policy/page articles INTO the agent identified by
  // `agentId`. `agentId` is the Knowledge Hub dropdown's currently-selected
  // agent (URL slug or `str(_id)` — backend accepts both via the resolver
  // ladder). Returns counts + `pages_capped` flag that drives the FE
  // cap-exceeded banner. Endpoint URL kept as `resync-content` for BE
  // compatibility; UX-level naming is now "Import Shopify Articles".
  importContent: (agentId: string) =>
    api.post<{
      ok: boolean;
      policies_synced: number;
      pages_synced: number;
      pages_capped: boolean;
      errors: string[];
    }>(`/api/agents/${agentId}/shopify/resync-content`),
};

// Public Widget
export const widgetAPI = {
  getConfig: (agentId: string) => api.get<WidgetConfig>(`/api/widget/config/${agentId}`),

  chat: (
    agentId: string,
    message: string,
    visitorId: string,
    sessionId?: string,
    visitorName?: string,
    attachments?: Array<{ s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string }>,
    channelInfo?: { channel?: string; shop_domain?: string; customer_id?: string | null; currency?: string },
    // Playground only: the widget SPA runs in the Playground iframe at
    // `/widget/{id}?source=playground`. Backend `_resolve_playground_source`
    // Gate 1 reads `request.query_params.get("source")` on the POST (FastAPI
    // parses query params on POST identically to GET), so the signal MUST
    // ride as a query param here — not just in the iframe URL — for the
    // conversation to be tagged `source=playground`. Never set for real
    // customer embeds (gated by caller).
    isPlayground?: boolean,
  ) =>
    // 🚨 NO `tool_calls` HERE, EVER. This is the CUSTOMER-FACING chat route —
    // the Playground merely shares it (`?source=playground`), so anything typed
    // onto this response is a field a real customer's browser can receive. Tool
    // payloads carry PII (emails, full Shopify order records). The field used to
    // exist and was always `[]`; it was removed backend-side (`88fb6e9`) and the
    // dead type went with it, because "the field exists, just populate it" is
    // exactly the mistake that would leak it. Tool data comes from the SEPARATE,
    // staff-authenticated trace endpoint
    // (`GET /api/widget/playground/conversation/{session_id}/trace`) — never
    // from this response, and never over the widget WS (which has no auth).
    api.post<{ session_id: string; messages: string[]; response_time_ms: number }>(
      '/api/widget/chat',
      {
        agent_id: agentId,
        message,
        visitor_id: visitorId,
        session_id: sessionId,
        visitor_name: visitorName,
        attachments,
        channel: channelInfo?.channel,
        shop_domain: channelInfo?.shop_domain,
        customer_id: channelInfo?.customer_id,
        currency: channelInfo?.currency,
      },
      isPlayground ? { params: { source: 'playground' } } : undefined,
    ),

  toolResult: (payload: {
    session_id: string;
    visitor_id: string;
    tool_name: string;
    status: 'success' | 'error';
    result?: any;
    error_code?: string | null;
    error_message?: string | null;
  }) => api.post('/api/widget/tool-result', payload),

  uploadAttachment: (agentId: string, sessionId: string, visitorId: string, file: File) => {
    const formData = new FormData();
    formData.append('agent_id', agentId);
    formData.append('session_id', sessionId);
    formData.append('visitor_id', visitorId);
    formData.append('file', file);
    return api.post<{ s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string }>('/api/widget/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Virtual Try-On (Phase 3) — poll-based. POST returns a job_id; the widget
  // then polls tryonStatus every ~2s until status is completed/error.
  tryon: (payload: TryOnRequest) =>
    api.post<{ job_id: string }>('/api/widget/tryon', payload),

  tryonStatus: (jobId: string) =>
    api.get<TryOnResult>(`/api/widget/tryon/${jobId}`),

  /**
   * Fetch widget chat history for an agent.
   *
   * Two call shapes:
   * 1. Customer iframe (`Widget.tsx`) passes a `visitorId` string — returns the
   *    visitor's own history, the existing v1 behavior.
   * 2. Playground (`Playground.tsx`) passes an options object with
   *    `{source: 'playground', staff_only: true}` — backend (Plan §B.5)
   *    returns conversations tagged `source=playground` for the current
   *    staff user via `staff_user_id` lookup. `visitor_id` is ignored when
   *    `staff_only` is set.
   *
   * Backend endpoint hasn't been audited for the new params yet — Storm's
   * Phase 1 (Plan §B.5) ships the source filter. If you see 422/400 on the
   * playground call, that's because Storm hasn't deployed yet.
   */
  getHistory: (
    agentId: string,
    visitorIdOrOpts:
      | string
      | { visitor_id?: string; source?: string; staff_only?: boolean },
  ) => {
    const params =
      typeof visitorIdOrOpts === 'string'
        ? { visitor_id: visitorIdOrOpts }
        : visitorIdOrOpts;
    return api.get<{
      conversations: {
        session_id: string;
        created_at: string;
        message_count: number;
        preview: string;
        // Plan-PlaygroundCollaboration §Backend-2: attribution + comments
        // (playground branch only; null on pre-fix legacy docs — FE
        // renders "Unknown / legacy" when created_by_name is null).
        created_by_id?: string | null;
        created_by_name?: string | null;
        comments?: ConversationComment[];
      }[];
    }>(`/api/widget/config/${agentId}/history`, { params });
  },

  /**
   * Plan-PlaygroundCollaboration §Backend-3 — add an internal comment to
   * a playground conversation. Mirrors `chatsAPI.addComment` /
   * `api/chats.py:add_comment`. `agentId` is the agent SLUG (same value
   * passed to `getHistory`). Staff-session-gated (cookie `prodagi_session`);
   * any workspace member may comment. 200 → the new ConversationComment
   * (`comment_id`-shaped, identical to chats). Fires a workspace-wide
   * `playground_comment` notification server-side.
   */
  addPlaygroundComment: (agentId: string, sessionId: string, text: string) =>
    // `?source=playground` is REQUIRED: backend `add_playground_comment`
    // gates via `_resolve_playground_source`, whose Gate 1 returns CUSTOMER
    // (→ 401) unless the REQUEST carries `?source=playground` (verified vs
    // shipped back/api/widget.py @ d3abcaf:79). This endpoint ONLY exists
    // for playground conversations, so the signal is unconditional here —
    // same fix class as the chat POST (commit c2b7784).
    api.post<ConversationComment>(
      `/api/widget/config/${agentId}/history/${sessionId}/comments`,
      { text },
      { params: { source: 'playground' } },
    ),

  /**
   * STAFF-ONLY tool-call trace for a Playground conversation.
   *
   * A SEPARATE, SEPARATELY-AUTHENTICATED door — deliberately not a field on the
   * customer chat response or the customer replay serializer. The gate is the
   * `prodagi_session` cookie + workspace membership on the agent; it does NOT
   * trust `?source=playground` (an attacker-controlled query param on a public
   * widget route). Tool payloads carry real customer PII, so the boundary holds
   * by construction rather than by a runtime `if`.
   *
   * Denial is **404, never 403** — a 403 would confirm the conversation exists.
   * So an anonymous visitor, an expired staff session, a non-member, an unknown
   * session, and a non-playground conversation are indistinguishable. Callers
   * must treat 404 as "no trace available", NOT as an error.
   *
   * Cookie-authed: `withCredentials` is already set on the shared axios
   * instance, so no header work here.
   */
  getPlaygroundTrace: (sessionId: string) =>
    api.get<PlaygroundTrace>(
      `/api/widget/playground/conversation/${sessionId}/trace`,
    ),

  // `isPlayground`: when the widget is running in the Playground iframe,
  // resuming a `source=playground` conversation MUST carry
  // `?source=playground`. Backend `get_conversation_detail` runs
  // `_resolve_playground_source` for playground-source convos and 404s
  // ("Conversation not found") when the request lacks it (verified vs
  // shipped back/api/widget.py @ e3333b7: get_conversation_detail §5b
  // gate + `_resolve_playground_source` line 79). Gated by the caller —
  // omitted for real customer embeds, so the request is byte-identical
  // to before when not in playground mode. Same fix class as c2b7784.
  getConversation: (sessionId: string, visitorId: string, isPlayground?: boolean) =>
    api.get<{ session_id: string; messages: { role: string; content: string; timestamp: string }[]; csat_score?: number | null; ticket_id?: string | null }>(`/api/widget/conversation/${sessionId}`, {
      params: isPlayground
        ? { visitor_id: visitorId, source: 'playground' }
        : { visitor_id: visitorId },
    }),

  /**
   * Plan-CSATUIModal.md §A.3 (2026-05-15) — submit a customer CSAT
   * response (rating + optional comment) OR an explicit decline.
   *
   * Submit path: `{rating: 1-5, comment?: string (≤2000)}` — backend
   * writes `csat_score` + `csat_comment` + `csat_timestamp`.
   *
   * Skip / decline path: `{declined: true}` — backend writes
   * `csat_declined: true` + `csat_timestamp`. Does NOT set csat_score,
   * so the conversation does NOT count as "Surveys Returned" in the
   * dashboard metric (preserves the meaningful completion ratio).
   *
   * Idempotent: a second POST for a conversation that already has a
   * csat_score or csat_declined returns 200 `{status: "already_recorded"}`
   * — the modal should treat that as success and transition to the
   * thanks state (do not show an error).
   *
   * `conversation_id` accepts either the Mongo `_id` or the
   * `session_id` per Storm's resolver (Plan §A.3).
   */
  submitCSAT: (
    conversationId: string,
    body: { rating?: number; comment?: string; declined?: boolean },
  ) =>
    api.post<{ status: 'recorded' | 'already_recorded' }>(
      `/api/widget/conversations/${conversationId}/csat`,
      body,
    ),

  // -------------------------------------------------------------------------
  // Email OTP verification — Contract-WidgetEmailOTP.md (FROZEN).
  //
  // Gates customer/order tools behind a verified email. The OTP never flows
  // through the agent: the widget opens its modal on the `verification_required`
  // flag in the chat response, and these three endpoints are the whole surface.
  //
  // 🚨 `request` ALWAYS resolves 200 {sent: true} — for unknown emails, rate-
  // limit hits and send failures alike. Anything else turns it into an
  // account-enumeration oracle. Never branch UI copy on this response, and
  // never tell the user whether the email exists.
  // -------------------------------------------------------------------------
  verifyRequest: (sessionId: string, agentId: string, email: string) =>
    api.post<{ sent: boolean }>('/api/widget/verify/request', {
      session_id: sessionId,
      agent_id: agentId,
      email,
    }),

  // The gate itself is enforced server-side inside the tool; `verified: false`
  // carries a reason so the modal can tell "wrong code" from "start over".
  verifyConfirm: (sessionId: string, email: string, code: string) =>
    api.post<{
      verified: boolean;
      reason?: 'invalid' | 'expired' | 'too_many_attempts';
    }>('/api/widget/verify/confirm', {
      session_id: sessionId,
      email,
      code,
    }),

  // `method` says HOW the session was verified. Null when unverified.
  verifyStatus: (sessionId: string) =>
    api.get<{
      verified: boolean;
      email: string | null;
      method: 'otp' | 'identity_token' | null;
    }>(
      '/api/widget/verify/status',
      { params: { session_id: sessionId } },
    ),

  // Identity passthrough — a partner host page signs its logged-in buyer as a
  // short-lived JWT (widget.js `data-identity-token` / `ProdagiWidget.identify`).
  // Verifying it marks the session verified exactly like a confirmed OTP, so the
  // gated tools stop asking.
  //
  // 🚨 `token` is a bearer credential: it travels in the POST BODY only — never
  // a query param (URLs reach load-balancer logs and Sentry breadcrumbs) — and
  // callers must never log it or the axios error (whose `config.data` holds it).
  verifyIdentity: (sessionId: string, agentId: string, token: string) =>
    api.post<
      | { verified: true; email: string; name: string | null; expires_at: number }
      | { verified: false; reason: 'invalid' | 'expired' }
    >('/api/widget/verify/identity', {
      session_id: sessionId,
      agent_id: agentId,
      token,
    }),

  // Host signed out (`identify(null)`) — drop the identity for this session.
  clearIdentity: (sessionId: string) =>
    api.post<{ cleared: boolean }>('/api/widget/verify/identity/clear', {
      session_id: sessionId,
    }),

  // Plan-HumanAgentHandoff-2026-06-09 §Backend endpoints — customer-facing,
  // anon-session-token auth, idempotent (15-min anti-spam window).
  // `agent_id` (the widget's agent slug) lets the BE create the
  // conversation on-demand when the customer requests a human on a fresh
  // session that hasn't sent a message yet (previously 404'd). Matches BE
  // RequestHumanBody.
  requestHuman: (sessionId: string, reason?: string, agentId?: string) =>
    api.post<{ handoff_status: 'requested' | 'active' | 'released' }>(
      `/api/widget/conversations/${sessionId}/request-human`,
      { reason: reason || 'customer_requested', agent_id: agentId },
    ),
  // Customer withdraws a still-pending human-handoff request and returns to
  // AI chat. Mirrors requestHuman (anon-session auth, agent_id lets the BE
  // resolve the conversation on-demand). Returns the resulting status —
  // 'active' means a staff member joined in the race, so the caller keeps the
  // human banner instead of stranding the customer.
  cancelHuman: (sessionId: string, agentId?: string) =>
    api.post<{ status: 'none' | 'active' | 'requested' }>(
      `/api/widget/conversations/${sessionId}/cancel-human`,
      { agent_id: agentId },
    ),
};

// Workspaces
export const workspaceAPI = {
  list: () => api.get('/api/workspaces/'),
  create: (data: { name: string }) => api.post('/api/workspaces/', data),
  get: (id: string) => api.get(`/api/workspaces/${id}`),
  update: (id: string, data: Partial<{ name: string }>) => api.patch(`/api/workspaces/${id}`, data),
  delete: (id: string) => api.delete(`/api/workspaces/${id}`),
  getMembers: (id: string) => api.get(`/api/workspaces/${id}/members`),
  updateMember: (wsId: string, userId: string, data: { roles: string[] }) =>
    api.patch(`/api/workspaces/${wsId}/members/${userId}`, data),
  removeMember: (wsId: string, userId: string) =>
    api.delete(`/api/workspaces/${wsId}/members/${userId}`),
  updateMemberAgentScope: (workspaceId: string, userId: string, data: { agent_scope: string; agent_ids: string[] }) =>
    api.put(`/api/workspaces/${workspaceId}/members/${userId}/agent-scope`, data),
  /**
   * `agent_scope`/`agent_ids` have always been accepted by `InviteRequest`
   * (api/workspaces.py) but were never sent — so an admin could only scope
   * someone by inviting first and editing after, for the one case (a
   * never-registered person) where the invitation is the only handle.
   *
   * 🚨 `agent_ids` holds `str(agent._id)` — the `.id` field — NOT the
   * `agent_id` slug, matching what a membership stores.
   *
   * 🚨 EVERY OPTIONAL FIELD HERE IS GATED BEHIND `rbac_custom_roles` AND MUST
   * BE OMITTED — not sent as null/'all'/[] — while the flag is off, so a
   * flag-off request body is byte-identical to the pre-gate one. The backend
   * ignores them when its own flag is off, but emitting them anyway would
   * advertise a policy that is not being enforced.
   */
  invite: (
    wsId: string,
    data: {
      email: string;
      roles: string[];
      full_name?: string;
      agent_scope?: 'all' | 'selected';
      agent_ids?: string[];
    },
  ) => api.post(`/api/workspaces/${wsId}/invite`, data),
  getInvitation: (token: string) => api.get(`/api/invitations/${token}`),
  acceptInvitation: (token: string) => api.post(`/api/invitations/${token}/accept`),
  acceptInvitationById: (id: string) => api.post(`/api/invitations/id/${id}/accept`),
  listMyInvitations: () => api.get('/api/invitations/mine'),
  listPendingInvitations: (wsId: string) =>
    api.get(`/api/workspaces/${wsId}/invitations/pending`),

  /**
   * Edit a PENDING invitation's access before the invitee registers, accepts,
   * or even has a user document — Plan-RBAC-SchemaRoles-2026-08.md §12.
   *
   * This works because `accept_invitation` builds the membership doc FROM the
   * invitation at accept time, so the invitation is the pre-registration
   * permission carrier. `agent_scope`/`agent_ids` are already read there but
   * are never set by the invite path, so this is also the only way to
   * pre-assign agent access to someone who has never signed up.
   *
   * 🚨 Fails distinguishably once the invitation is accepted or expired: at
   * that point it is a dead document and the membership governs instead.
   * Callers must surface WHICH of those happened — see `classifyInviteError`
   * in Team.tsx. Reporting success against a dead invitation would tell an
   * admin they changed access when they changed nothing.
   */
  updateInvitation: (
    wsId: string,
    invitationId: string,
    body: { roles?: string[]; agent_scope?: 'all' | 'selected'; agent_ids?: string[] },
  ) => api.patch<PendingInvitation>(
    `/api/workspaces/${wsId}/invitations/${invitationId}`,
    body,
  ),

  // Plan-AutoInstallCommerceAgent §5d / §6 (2026-05-26) — flip the
  // workspace's designated Shopify agent (Phase B reassignment).
  //
  // Contract per Storm's 2026-05-26 confirmation:
  //   Body  : { agent_id: <str(_id)> }  -- MongoDB ObjectId-as-string,
  //           NEVER the URL slug (two-identifier rule).
  //   200   : { status: "reassigned" | "noop",
  //             shopify_agent_id: string,
  //             agent_slug: string,
  //             moved: { integrations: number, knowledge_sources: number } }
  //   403   : caller is not workspace owner/admin.
  //   404   : target agent doesn't belong to the workspace.
  //   409   : workspace billing_provider != "shopify".
  reassignShopifyAgent: (workspaceId: string, agentId: string) =>
    api.patch<{
      status: 'reassigned' | 'noop';
      shopify_agent_id: string;
      agent_slug: string;
      moved: { integrations: number; knowledge_sources: number };
    }>(`/api/workspaces/${workspaceId}/shopify-agent`, {
      agent_id: agentId,
    }),
};

// Integrations (Data Connectors)
export interface InputParam {
  name: string;
  type: 'string' | 'integer' | 'number';
  required: boolean;
  description: string;
  // `identity` fills the value from the session's VERIFIED identity (partner
  // identity token), never from the model — `identity_claim` says which claim.
  source: 'customer' | 'context' | 'static' | 'identity';
  ask_prompt: string;
  example: string;
  static_value: string;
  // Optional so params saved before identity passthrough keep parsing.
  identity_claim?: IdentityClaim | null;
}

export type IdentityClaim = 'email' | 'user_id' | 'name' | 'mkt';

export interface DataConnector {
  _id: string;
  workspace_id: string;
  name: string;
  description: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT';
  auth_type: 'none' | 'bearer' | 'api_key' | 'basic';
  auth_header_name: string;
  auth_source: 'encrypted' | 'input_param';
  auth_param_name: string | null;
  has_auth: boolean;
  auth_masked?: string;
  input_params: InputParam[];
  custom_headers: { name: string; value: string }[];
  saved_response?: any;
  use_saved_response: boolean;
  body_type: 'none' | 'json' | 'form';
  body_template: string | null;
  returns_description: string | null;
  depends_on: string | null;
  projection: string[];
  agent_scope: 'all' | 'selected';
  agent_ids: string[];
  is_active: boolean;
  call_count: number;
  last_called_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  // `shopify_template` / `spago_template` rows are installed and managed by an
  // integration package, not typed by hand — hidden from the custom list.
  source?: string | null;
  // Package rows only: the owning integration and the row's stable key within
  // the package (updates match on this, never on name).
  integration_id?: string | null;
  template_key?: string | null;
  // Connector Card Rendering (Plan-ConnectorCardRendering.md) — optional so
  // rows written before the feature shipped keep parsing.
  render_as?: RenderAs;
  card_mapping?: CardMapping | null;
  card_mapping_source?: CardMappingSource | null;
  card_mapping_confirmed_at?: string | null;
  // Identity passthrough — opt-in per connector. When on, the session's raw
  // partner identity token is sent in `identity_header_name` alongside the
  // connector's own credential. Optional so older rows parse; absent ⇒ off.
  forward_identity_token?: boolean;
  // null (or absent) = the connector uses the platform's standard header.
  identity_header_name?: string | null;
  // No sign-in URL here by design: an unsigned-in call still reaches the
  // partner (without the token), and the partner's 401 carries its own
  // per-marketplace `login_url`, which the agent passes on.
}

export type ConnectorPayload = Omit<DataConnector, '_id' | 'workspace_id' | 'has_auth' | 'auth_masked' | 'call_count' | 'last_called_at' | 'last_error' | 'created_at' | 'updated_at'> & {
  auth_value?: string;
  custom_headers?: { name: string; value: string }[];
  saved_response?: any;
  use_saved_response?: boolean;
};

// Identity verification keys — the partner's per-API-key signing secrets used
// to verify identity tokens. 🚨 The secret is WRITE-ONLY: it is sent once on
// create and never returned by any endpoint, so this type has no secret field.
export interface IdentityKey {
  id: string;
  label: string;
  issuer: string;
  kid: string;
  created_at: string;
  last_used_at: string | null;
}

export const identityKeysAPI = {
  list: () => api.get<IdentityKey[]>('/api/integrations/identity-keys'),
  // 409 ⇒ a key with that `kid` already exists in the workspace.
  create: (data: { label: string; kid: string; secret: string; issuer?: 'spago' }) =>
    api.post<IdentityKey>('/api/integrations/identity-keys', data),
  delete: (id: string) => api.delete(`/api/integrations/identity-keys/${id}`),
};

// ── Spago integration package ──────────────────────────────────────────────
// Plan-SpagoIntegration.md. One Spago integration per workspace installs a
// fixed, reviewed set of connectors plus the identity key in one step. Every
// route is two segments deep under /api/integrations/spago (a one-segment path
// would be captured by the generic `/{connector_id}` routes). Every error
// `detail` is a plain string — show it inline. 🚨 Secrets (`api_key`,
// `identity_secret`) are write-only: sent here, never returned by `status`.

export type SpagoEnvironment = 'staging' | 'production';

export interface SpagoEnvironmentOption {
  id: SpagoEnvironment;
  label: string;
  /** False ⇒ show the option disabled (production until Spago goes live). */
  available: boolean;
}

export interface SpagoPackageConnector {
  template_key: string;
  /** Tool name, e.g. `spago_get_my_orders`. */
  name: string;
  title: string;
  summary: string;
  is_active: boolean;
  /** True ⇒ answers for the signed-in shopper (identity forwarded). */
  uses_identity: boolean;
  render_as: RenderAs;
  required_scope: string;
  /** Why the package ships this connector switched off (e.g. promotions); null otherwise. */
  default_off_reason: string | null;
}

/** An active connector the owner typed by hand that also calls a Spago host. */
export interface SpagoHandmadeConnector {
  id: string;
  name: string;
  endpoint_host: string;
  is_active: boolean;
}

export interface SpagoIntegrationStatus {
  connected: boolean;
  environment: SpagoEnvironment | null;
  environments: SpagoEnvironmentOption[];
  /** Agent `_id` strings (`Agent.id`) that get the order/account connectors. */
  buyer_agent_ids: string[];
  catalog_agent_scope: 'all' | 'selected';
  catalog_agent_ids: string[];
  package_version: number | null;
  latest_version: number;
  update_available: boolean;
  key_id: string | null;
  has_api_key: boolean;
  connectors: SpagoPackageConnector[];
  handmade_connectors: SpagoHandmadeConnector[];
  /** Non-blocking warnings from the most recent successful connect/update (e.g.
   *  the storefront signs with a newer key). Always present; `[]` when none.
   *  Shown as amber notices; they clear on the next successful update. */
  warnings: string[];
}

export interface SpagoConnectBody {
  environment: SpagoEnvironment;
  api_key: string;
  key_id: string;
  identity_secret: string;
  /** At least one. Agent `_id` strings. */
  buyer_agent_ids: string[];
  /** null = all agents. */
  catalog_agent_ids: string[] | null;
}

/** Every field optional: omitted ⇒ unchanged. Empty body ⇒ just re-apply the latest package. */
export interface SpagoUpdateBody {
  api_key?: string;
  key_id?: string;
  identity_secret?: string;
  buyer_agent_ids?: string[];
  catalog_agent_ids?: string[] | null;
}

export const spagoIntegrationAPI = {
  status: () => api.get<SpagoIntegrationStatus>('/api/integrations/spago/status'),
  // 422 {detail} on a bad/short key or secret, missing buyer agent, unavailable environment.
  connect: (body: SpagoConnectBody) =>
    api.post<SpagoIntegrationStatus>('/api/integrations/spago/connect', body),
  update: (body?: SpagoUpdateBody) =>
    api.post<SpagoIntegrationStatus>('/api/integrations/spago/update', body),
  setConnectorActive: (templateKey: string, isActive: boolean) =>
    api.patch<SpagoIntegrationStatus>(
      `/api/integrations/spago/connectors/${encodeURIComponent(templateKey)}`,
      { is_active: isActive },
    ),
  turnOffHandmade: (connectorId: string) =>
    api.post<SpagoIntegrationStatus>(
      `/api/integrations/spago/handmade/${encodeURIComponent(connectorId)}/turn-off`,
    ),
  // `remove_key=true` also deletes the identity verification key.
  disconnect: (removeKey: boolean) =>
    api.delete<{ disconnected: boolean }>('/api/integrations/spago/connection', {
      params: { remove_key: removeKey },
    }),
};

export const integrationsAPI = {
  list: () => api.get<DataConnector[]>('/api/integrations/'),
  create: (data: ConnectorPayload) => api.post<DataConnector>('/api/integrations/', data),
  get: (id: string) => api.get<DataConnector>(`/api/integrations/${id}`),
  update: (id: string, data: Partial<ConnectorPayload>) => api.put<DataConnector>(`/api/integrations/${id}`, data),
  delete: (id: string) => api.delete(`/api/integrations/${id}`),
  test: (id: string, inputs: Record<string, string>) =>
    api.post<{ status_code: number; data: Record<string, unknown> }>(`/api/integrations/${id}/test`, { inputs }),
  saveProjection: (id: string, fields: string[]) =>
    api.put<DataConnector>(`/api/integrations/${id}/projection`, { fields }),
  toggle: (id: string, is_active: boolean) =>
    api.patch<DataConnector>(`/api/integrations/${id}/toggle`, { is_active }),
  // 400 when the connector has no saved_response yet ("Run Test first").
  // Body key `card_type` per back/api/integrations.py InferCardMappingRequest
  // (optional there — falls back to the connector's saved render_as — but the
  // FE always sends the dropdown value so inference can run before saving).
  inferCardMapping: (id: string, cardType: RenderAs) =>
    api.post<InferCardMappingResponse>(`/api/integrations/${id}/infer-card-mapping`, { card_type: cardType }),
};

// Reports
export const reportsAPI = {
  generate: (query: string, dateRange?: { start_date?: string; end_date?: string; days?: number }) =>
    api.post('/api/reports/generate', { query, ...dateRange }),
  sendEmail: (data: { recipient_emails: string[]; report_content: string; query: string; visualizations?: unknown[] }) =>
    api.post('/api/reports/send-email', data),
  getRecipients: () => api.get('/api/reports/recipients'),
};

// Orchestrator (Phase 1a) — Contract-OrchestratorPhase1a.md.
// `/api/reports` above stays live and behaviourally unchanged; the hub
// dispatches to the same Reports agent through its own route.
export const orchestratorAPI = {
  /**
   * Base URL for the SSE chat endpoint — pass to `streamOrchestratorChat`.
   * NOT an axios call: SSE needs `fetch` streaming, which bypasses the axios
   * request interceptor, so the stream helper re-attaches the auth headers
   * itself. Same split as `knowledgeAIAPI.draftUrl()`.
   */
  chatUrl: (): string => `${API_URL_FULL}/api/orchestrator/chat`,

  /** GET /api/orchestrator/runs — recent runs for the active workspace. */
  getRuns: (limit = 20) =>
    api.get<OrchestratorRunsResponse>('/api/orchestrator/runs', { params: { limit } }),

  /**
   * GET /api/orchestrator/runs/{run_id} — the full transcript of one past run
   * for the active workspace. 404 → not found / not in this workspace.
   *
   * v1 replays TEXT ONLY (charts and action cards are not reconstructed from
   * history), so the caller maps each message to a single text turn.
   */
  getRun: (runId: string) =>
    api.get<OrchestratorRunDetail>(
      `/api/orchestrator/runs/${encodeURIComponent(runId)}`,
    ),

  /**
   * GET /api/orchestrator/capabilities — the capability registry
   * (`back/orchestrator/capabilities.py`), read-only, no database.
   *
   * 🚨 This is what the empty state's suggested prompts are built from. It is
   * NOT decoration: a chip is rendered only if the tool behind it appears in
   * this response, so a capability that gets removed from the registry stops
   * being advertised on the very next load, with no frontend change. The
   * alternative — a hardcoded chip list — is the prose-drift failure the
   * registry was created to kill, moved one tier up.
   *
   * ⚠️ Ships ahead of the backend. `404` until the endpoint deploys, so the
   * caller MUST treat "no capabilities" as a normal state and show nothing
   * rather than falling back to a list of its own invention.
   */
  getCapabilities: () =>
    api.get<OrchestratorCapabilitiesResponse>('/api/orchestrator/capabilities'),

  /**
   * POST /api/orchestrator/actions/{id}/confirm — the SOLE mutator for a
   * Mode-B action. Tools only ever park a preview; this call is what writes.
   *
   * `validateStatus` lets the documented refusal bodies (`404
   * action_not_found`, `409 action_already_applied`, and any `{ok:false, code,
   * message}`) arrive as a normal resolved response instead of an axios throw.
   * Two reasons that matters here:
   *   - 409 means the write ALREADY SUCCEEDED (idempotency by `action_id`), so
   *     it must reach the caller as data to be presented as success — routing
   *     it through a catch block invites showing it as a failure.
   *   - the caller branches on `code` for every one of these, and a thrown
   *     `AxiosError` buries it two levels deep.
   *
   * 🚨 401 and 429 are deliberately left REJECTING: the response interceptor
   * above attaches global behaviour to both (session logout / the typed
   * `RateLimitError` + toast event), and that interceptor only runs on the
   * rejection path. Swallowing them here would silently disable session expiry
   * on this one endpoint. 5xx and network faults reject too — together with a
   * possibly-applied write that is exactly the retryable set, and retrying is
   * safe precisely because a replay comes back 409.
   */
  confirmAction: (actionId: string) =>
    api.post<OrchestratorActionConfirmResponse>(
      `/api/orchestrator/actions/${encodeURIComponent(actionId)}/confirm`,
      {},
      {
        validateStatus: (status) =>
          status < 500 && status !== 401 && status !== 429,
      },
    ),
};

// Help Center (agent-scoped)
export const helpCenterAPI = {
  // Settings
  getSettings: (agentId?: string) => api.get<HCSettings>('/api/help-center/settings', { params: agentId ? { agent_id: agentId } : undefined }),
  updateSettings: (data: Partial<HCSettings>) => {
    const { agent_id, ...body } = data;
    return api.patch<HCSettings>('/api/help-center/settings', body, { params: agent_id ? { agent_id } : undefined });
  },
  uploadAsset: (file: File, purpose: 'logo' | 'favicon') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', purpose);
    return api.post<{ url: string }>('/api/help-center/upload-asset', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Collections
  getCollections: (agentId?: string) =>
    api.get<HelpCenterCollection[]>('/api/help-center/collections', { params: agentId ? { agent_id: agentId } : undefined }),
  createCollection: (data: { name: string; emoji?: string; visibility?: string; order?: number }) =>
    api.post<HelpCenterCollection>('/api/help-center/collections', data),
  updateCollection: (id: string, data: Partial<{ name: string; emoji: string; visibility: string; order: number }>) =>
    api.patch<HelpCenterCollection>(`/api/help-center/collections/${id}`, data),
  deleteCollection: (id: string) => api.delete(`/api/help-center/collections/${id}`),
  reorderCollections: (items: { id: string; order: number }[]) =>
    api.patch('/api/help-center/collections/reorder', { items }),
};

// Knowledge AI (SSE + JSON endpoints)
export interface ArticleSuggestion {
  conversation_id: string;
  suggested_title: string;
  excerpt: string;
  created_at: string;
}

const API_URL_FULL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const knowledgeAIAPI = {
  /** Base URL for the draft SSE endpoint — pass to streamSSE */
  draftUrl: (): string => `${API_URL_FULL}/api/knowledge/ai-draft`,

  /** Base URL for the improve SSE endpoint — pass to streamSSE */
  improveUrl: (id: string): string => `${API_URL_FULL}/api/knowledge/${id}/ai-improve`,

  /** Base URL for draft-from-suggestion SSE endpoint — pass to streamSSE */
  draftFromSuggestionUrl: (conversationId: string): string =>
    `${API_URL_FULL}/api/knowledge/article-suggestions/${conversationId}/draft`,

  /** POST /api/knowledge/{id}/ai-seo — returns { meta_title, meta_description } */
  seo: (id: string, data: { body: string }) =>
    api.post<{ meta_title: string; meta_description: string }>(`/api/knowledge/${id}/ai-seo`, data),

  /** GET /api/knowledge/article-suggestions */
  getSuggestions: () => api.get<ArticleSuggestion[]>('/api/knowledge/article-suggestions'),

  /** POST /api/knowledge/article-suggestions/{conversationId}/dismiss */
  dismissSuggestion: (conversationId: string) =>
    api.post(`/api/knowledge/article-suggestions/${conversationId}/dismiss`),

  /** POST /api/knowledge/{id}/ai-split — enqueues async split, returns 202 */
  split: (id: string, body: string) =>
    api.post<{ status: string; knowledge_id: string }>(
      `/api/knowledge/${id}/ai-split`,
      { body }
    ),

  /**
   * POST /api/knowledge/analyze-video — Plan-VideoAnalysis.md §C.1.
   *
   * Synchronous Gemini-backed analysis of a YouTube URL or an uploaded
   * video (S3 key). Backend resolves the workspace, checks the
   * `max_video_analyses` quota, calls `gemini-3-flash-preview`, and
   * returns structured HTML the caller writes into the editor.
   *
   * Pass EITHER `video_url` (YouTube only — Gemini fetches server-side)
   * OR `video_s3_key` (uploaded file — backend streams S3 → Gemini File
   * API). Never both.
   *
   * Documented error codes (HTTP):
   *   - 402 quota_exceeded     → "max_video_analyses" hit for the cycle
   *   - 413 file_too_large     → exceeds plan-tier cap (B.2)
   *   - 415 unsupported_source → non-YouTube URL when we sent `video_url`
   *   - 504 analysis_timeout   → Gemini call >90s
   * Frontend never blocks the underlying save on these — see error UX
   * in Plan-VideoAnalysis.md §C.2.
   */
  analyzeVideo: (data: {
    video_url?: string;
    video_s3_key?: string;
    agent_id: string;
    /** Only `'structured_html'` ships in v1; the type leaves room for
     *  the future `summary_block` / `transcript` formats. */
    format?: 'structured_html' | 'summary_block' | 'transcript';
  }) =>
    api.post<{
      content_html: string;
      tokens_used: number;
      duration_seconds: number;
    }>('/api/knowledge/analyze-video', {
      ...data,
      format: data.format ?? 'structured_html',
    }),
};

export default api;

// Profile
export const profileAPI = {
  getProfile: () => api.get('/api/profile/me'),
  updateProfile: (data: {
    full_name?: string;
    company_name?: string;
    email?: string;
    current_password?: string;
    new_password?: string;
  }) => api.put('/api/profile/me', data),
  upgradePlan: (plan: string) => api.post('/api/profile/upgrade', { plan }),
};

// Contacts
export const contactsAPI = {
  list: (label?: string) => api.get<any[]>('/api/contacts/', { params: { label } }),
  get: (contactId: string) => api.get<any>(`/api/contacts/${contactId}`),
  create: (data: any) => api.post<any>('/api/contacts/', data),
  update: (contactId: string, data: any) => api.put<any>(`/api/contacts/${contactId}`, data),
  delete: (contactId: string) => api.delete(`/api/contacts/${contactId}`),
};

// Stripe
export async function openBillingPortal(stripeCustomerId: string): Promise<string> {
  const response = await api.post<{ portal_url: string }>('/api/stripe/billing-portal', {
    stripe_customer_id: stripeCustomerId,
  });
  return response.data.portal_url;
}


// Billing (workspace-scoped, Phase A)
import type {
  WorkspaceBilling,
  ProrationPreview,
  ChangePlanResponse,
  Plan,
  SubscriptionStatus,
  SwapRequest,
  BillingInterval,
  LiveAvatarTier,
  LiveAvatarAddon,
  LiveAvatarProrationPreview,
  LiveAvatarChangeResponse,
  TextBundleCheckoutRequest,
  SimpleBundleCheckoutRequest,
  BundleCheckoutResponse,
  ActiveCredit,
  InvoiceLedgerResponse,
  CancelSubscriptionResponse,
  ResumeSubscriptionResponse,
  TextTopUpHistoryResponse,
} from '../types/billing';
// Value import (the block above is `import type`) — the top-up routes are
// runtime constants, deliberately not inlined. See TEXT_TOPUP_ENDPOINT.
import { TEXT_TOPUP_ENDPOINT, TEXT_TOPUP_HISTORY_ENDPOINT } from '../types/billing';

export const billingAPI = {
  getWorkspaceBilling: (workspaceId: string) =>
    api.get<WorkspaceBilling>(`/api/billing/workspace/${workspaceId}`),

  // Plan-InvoiceLedgerAndUpcomingChargeEmail-2026-08.md §3 — paginated
  // billing ledger. Gated behind BILLING_INVOICE_HISTORY_ENABLED on the
  // backend: while the gate is off this 404s (deliberately — an empty 200
  // would be a confident zero), so callers must feature-detect on
  // `user.features.billing_invoice_history` before calling it.
  listInvoices: (workspaceId: string, opts?: { limit?: number; cursor?: string | null }) => {
    const params = new URLSearchParams({ limit: String(opts?.limit ?? 20) });
    if (opts?.cursor) params.set('cursor', opts.cursor);
    return api.get<InvoiceLedgerResponse>(
      `/api/billing/workspace/${workspaceId}/invoices?${params.toString()}`,
    );
  },

  previewChange: (workspaceId: string, targetPlan: Plan, billingInterval: BillingInterval = 'month') =>
    api.post<ProrationPreview>(`/api/stripe/preview-change`, {
      workspace_id: workspaceId,
      target_plan: targetPlan,
      billing_interval: billingInterval,
    }),

  changePlan: (workspaceId: string, targetPlan: Plan, billingInterval: BillingInterval = 'month') =>
    api.post<ChangePlanResponse>(`/api/stripe/change-plan`, {
      workspace_id: workspaceId,
      target_plan: targetPlan,
      billing_interval: billingInterval,
    }),

  createCheckoutSession: (workspaceId: string, plan: Plan, billingInterval: BillingInterval = 'month') =>
    api.post<{ checkout_url: string }>(`/api/stripe/create-checkout-session`, {
      workspace_id: workspaceId,
      plan,
      billing_interval: billingInterval,
    }),

  cancelDowngrade: (workspaceId: string) =>
    api.post<ChangePlanResponse>(`/api/stripe/cancel-downgrade`, {
      workspace_id: workspaceId,
    }),

  // ─── Self-serve cancellation ──────────────────────────────────────
  //
  // 🚨 NOT `cancelDowngrade` above, despite the adjacent names.
  // `cancelDowngrade` UNDOES a scheduled downgrade and keeps the customer
  // paying. These two END the subscription at period end / undo that.
  //
  // Replaces a dead top-level `cancelSubscription()` that posted to
  // `/api/stripe/cancel` — a route the backend never had, and which nothing
  // called. Removed rather than left beside this one.
  cancelSubscription: (workspaceId: string) =>
    api.post<CancelSubscriptionResponse>(`/api/stripe/cancel-subscription`, {
      workspace_id: workspaceId,
    }),

  resumeSubscription: (workspaceId: string) =>
    api.post<ResumeSubscriptionResponse>(`/api/stripe/resume-subscription`, {
      workspace_id: workspaceId,
    }),

  applySwap: (workspaceId: string, swap: SwapRequest) =>
    api.post<WorkspaceBilling>(`/api/billing/workspace/${workspaceId}/swap`, swap),

  toggleAutoOverage: (workspaceId: string, resource: string, enabled: boolean) =>
    // Backend `AutoOverageRequest` (back/api/billing.py:44-45) expects
    // `{toggles: Dict[str, bool]}`. Previous shape `{resource, enabled}`
    // 422'd at the Pydantic layer. The handler at billing.py:510-518
    // merges the partial dict into the existing workspace.auto_overage
    // map, so a single-key payload here flips just that one resource
    // without touching the rest.
    api.post<WorkspaceBilling>(`/api/billing/workspace/${workspaceId}/auto-overage`, {
      toggles: { [resource]: enabled },
    }),

  // ─── Bundles (Plan-BundlesImplementation §E.1 + §E.2) ─────────────
  //
  // Contract verified against `back/api/billing_bundles.py` post-Storm's
  // Phase 2 ship. `workspace_id` lives in the request BODY (POST) or
  // QUERY (GET) — NOT the X-Workspace-ID header (Storm's role-gate uses
  // `workspace_members.workspace_id` lookups keyed off the body/query).

  buyTextBundle: (req: TextBundleCheckoutRequest) =>
    api.post<BundleCheckoutResponse>(`/api/billing/bundles/text`, {
      workspace_id: req.workspace_id,
      // Storm's TextBundleAllocation Pydantic model defaults all 5
      // resources to 0 if omitted, so send a full allocation with
      // zeros filled in for clarity (matches backend validator).
      allocation: {
        chat_conversations: 0,
        email_threads: 0,
        knowledge_summaries: 0,
        web_scrapes: 0,
        reports: 0,
        ...req.allocation,
      },
    }),

  /** Pooled-mode Text Top-Up — $10 / 2,000,000 AI text tokens, FLAT.
   *  No `allocation` body: the pool is one number, there is nothing to
   *  split. Round-trips through the SAME `?bundle_session=` success URL
   *  as every other bundle, so the return handler at BillingPage.tsx:587
   *  needs no new branch.
   *
   *  Path comes from `TEXT_TOPUP_ENDPOINT` rather than being inlined like
   *  its siblings; the route is verified against the shipped backend.
   *
   *  🚨 A DIFFERENT PRODUCT from `buyTextBundle`, not a mode of it. That one
   *  sells the legacy $5 allocation bundle and fans out `workspace_credits`
   *  rows; this grants a flat block into the pool and never touches
   *  `workspace_credits`. Both stay live — the $5 SKU is the rollback path. */
  buyTextTopUp: (req: SimpleBundleCheckoutRequest) =>
    api.post<BundleCheckoutResponse>(TEXT_TOPUP_ENDPOINT, {
      workspace_id: req.workspace_id,
    }),

  /** Top-up purchase history + the workspace's standing balance. Owner and
   *  admin only; 404s while `TEXT_POOL_BILLING_ENABLED` is off, same as the
   *  purchase route. `workspace_id` is a QUERY param, matching
   *  `listActiveCredits` rather than the POST routes' body. */
  listTextTopUps: (workspaceId: string, opts?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams({ workspace_id: workspaceId });
    // Sent only when supplied — the backend defaults to limit 50 / offset 0,
    // and it 400s on a limit outside [1,200], so never send a bare 0.
    if (typeof opts?.limit === 'number') params.set('limit', String(opts.limit));
    if (typeof opts?.offset === 'number') params.set('offset', String(opts.offset));
    return api.get<TextTopUpHistoryResponse>(
      `${TEXT_TOPUP_HISTORY_ENDPOINT}?${params.toString()}`
    );
  },

  buyVoiceBundle: (req: SimpleBundleCheckoutRequest) =>
    api.post<BundleCheckoutResponse>(`/api/billing/bundles/voice`, {
      workspace_id: req.workspace_id,
    }),

  buyLiveAvatarBundle: (req: SimpleBundleCheckoutRequest) =>
    api.post<BundleCheckoutResponse>(`/api/billing/bundles/liveavatar`, {
      workspace_id: req.workspace_id,
    }),

  /** List active (non-expired, non-exhausted) credits for a workspace.
   *  Response is `{credits: ActiveCredit[]}` — Storm wraps the array
   *  in `back/api/billing_bundles.py:518` so future schema additions
   *  don't break the bare-array contract. Callers should read
   *  `res.data.credits`. */
  listActiveCredits: (workspaceId: string) =>
    api.get<{ credits: ActiveCredit[] }>(
      `/api/billing/credits?workspace_id=${encodeURIComponent(workspaceId)}`
    ),

  // ─── Shopify Managed Pricing (shop build only) ────────────────────
  //
  // Plan-ShopifyManagedPricingBilling.md (Phase 3 billing slice). These
  // two endpoints replace the Stripe checkout / portal flow for
  // workspaces whose `billing_provider == "shopify"`. Both are
  // session-gated on the backend (resolve the caller's active Shopify
  // workspace; assert provider) — no workspace_id arg needed. Contract
  // mirrors `back/api/billing_shopify.py` (managed-pricing-entry +
  // reconcile). Only ever called from the shopify build (the calling
  // sites gate on APP_CONFIG.billingProvider === 'shopify').

  /** Resolve the Shopify-hosted Managed Pricing URL for the caller's
   *  workspace. The merchant picks / changes / cancels their plan on
   *  Shopify's hosted page. On success returns `{managed_pricing_url}`;
   *  if the app slug isn't configured yet the backend 503s with
   *  `{code: "shopify_managed_pricing_not_configured"}` (the FE shows a
   *  friendly "billing not configured yet" message). */
  shopifyManagedPricingEntry: () =>
    api.get<{ managed_pricing_url: string }>(
      '/api/billing/shopify/managed-pricing-entry'
    ),

  /** Plan-SwitchToBillingAPI.md — Billing API flow. Backend calls the
   *  `appSubscriptionCreate` GraphQL mutation for the requested plan and
   *  returns the merchant-approval confirmation URL. FE redirects the
   *  whole window to it; Shopify drives the change back via the existing
   *  `app_subscriptions/update` webhook. Session-gated, shopify-workspace
   *  only. Returns 410 GONE when `SHOPIFY_BILLING_MODE=managed`.
   *
   *  `interval` is `'month'` (default) or `'year'`. Storm ships the BE
   *  yearly handling separately (task #8); until then the BE ignores
   *  `interval` and defaults to monthly. Non-blocking by design. */
  createShopifySubscription: (
    plan: 'starter' | 'pro' | 'business',
    interval: 'month' | 'year' = 'month',
  ) =>
    api.post<{ confirmation_url: string; subscription_id: string }>(
      '/api/billing/shopify/create-subscription',
      { plan, interval }
    ),

  /** Self-heal plan drift by re-querying Shopify for the workspace's
   *  authoritative subscription state and re-stamping the workspace if
   *  it differs. Called on Billing-page mount (shopify build). Returns
   *  the resolved plan/status snapshot. */
  shopifyReconcile: () =>
    api.post<{
      plan: Plan;
      subscription_status: SubscriptionStatus;
      trial_ends_at?: string | null;
      liveavatar_addon?: LiveAvatarAddon | null;
    }>('/api/billing/shopify/reconcile'),

  /** Cancel the merchant's active Shopify app subscription via the
   *  `appSubscriptionCancel` GraphQL mutation. Owner-only, session-gated,
   *  shopify-workspace only. Empty body POST — backend resolves the
   *  caller's workspace + active subscription gid from the session.
   *
   *  Success: 200 with `{status: "cancelled", subscription_gid, billing_cycle_end}`
   *  — merchant retains access through `billing_cycle_end` (Shopify
   *  proration default). Backend stamps `subscription_status='cancelled'`
   *  synchronously; the `plan` field stays put until the
   *  `app_subscriptions/update` webhook drives further transitions.
   *
   *  Errors:
   *    409 `{code: "no_active_subscription"}` — nothing to cancel.
   *    410 `{code: "shopify_managed_pricing"}` — backend mode flipped to
   *        managed; merchants must cancel via the Shopify admin / hosted
   *        pricing page instead of the Billing API.
   *    400 `{code: "shopify_user_errors", errors: [{field, message}, ...]}`
   *        — Shopify GraphQL userErrors surfaced verbatim. */
  cancelShopifySubscription: () =>
    api.post<{
      status: 'cancelled';
      subscription_gid: string;
      billing_cycle_end: string | null;
    }>('/api/billing/shopify/cancel-subscription'),
};

export const liveAvatarAPI = {
  subscribe: (workspaceId: string, tier: LiveAvatarTier) =>
    api.post<LiveAvatarChangeResponse>(`/api/stripe/liveavatar/subscribe`, {
      workspace_id: workspaceId,
      tier,
    }),

  previewChange: (workspaceId: string, tier: LiveAvatarTier) =>
    api.post<LiveAvatarProrationPreview>(`/api/stripe/liveavatar/preview-change`, {
      workspace_id: workspaceId,
      tier,
    }),

  changeTier: (workspaceId: string, tier: LiveAvatarTier) =>
    api.post<LiveAvatarChangeResponse>(`/api/stripe/liveavatar/change-tier`, {
      workspace_id: workspaceId,
      tier,
    }),

  cancel: (workspaceId: string) =>
    api.post<LiveAvatarChangeResponse>(`/api/stripe/liveavatar/cancel`, {
      workspace_id: workspaceId,
    }),

  cancelScheduledChange: (workspaceId: string) =>
    api.post<LiveAvatarChangeResponse>(`/api/stripe/liveavatar/cancel-scheduled-change`, {
      workspace_id: workspaceId,
    }),
};

// Avatar Service (back/ proxy → avatar-service)
//
// Two endpoints living on back/ that proxy/wrap the avatar-service:
//   1. POST /api/avatar/reference     — upload an idempotent reference
//      image OR video to GCS for a given agent. back/ verifies agent
//      ownership via _get_agent_for_user, then streams to GCS, then
//      pings avatar-service /internal/cache/invalidate.
//   2. POST /api/avatar/session-token — mint a 5-minute HS256 JWT bound
//      to (agent_id, workspace_id, session_id) for the live avatar
//      WebRTC session. Replaces the old "Python one-liner + admin_key"
//      manual flow.
//
// The actual /sessions + /healthz + signaling-WS calls stay
// direct-to-avatar-service (different host) and are issued via raw
// `fetch()` from the AvatarTest page — they're real-time and live
// outside the back/ axios client.
export const avatarAPI = {
  uploadReference: (agentId: string, file: File) => {
    const formData = new FormData();
    formData.append('agent_id', agentId);
    formData.append('file', file);
    return api.post<{
      kind: 'image' | 'video';
      gcs_uri: string;
      bytes: number;
      duration_seconds?: number;
      cache_invalidated?: boolean;
    }>('/api/avatar/reference', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Mint a 5-min HS256 JWT bound to (agent_id, workspace_id, session_id).
  // workspace_id is derived server-side from the active-workspace header
  // (X-Workspace-ID, auto-injected by the axios interceptor) — do NOT pass
  // it in the body. session_id is optional; back/ generates uuid4 when
  // omitted and returns it so the subsequent /sessions POST body matches
  // the JWT claim.
  mintSessionToken: (agentId: string, sessionId?: string) =>
    api.post<{
      token: string;
      session_id: string;
      expires_at: number;
    }>('/api/avatar/session-token', {
      agent_id: agentId,
      ...(sessionId ? { session_id: sessionId } : {}),
    }),
};

// --- AI Stylist catalog-sync API ----------------------------------------
// Backs the "Sync catalog for Stylist" card on the Stylist page. For large
// Shopify stores (>=250 in-stock products) the catalog is mirrored into our
// DB so the Stylist searches our exact grouping instead of a 250-item sample;
// small stores stay on the live flow ("mode":"live", no sync needed).
//
// Contract (files/recs/Plan-StylistCatalogSync.md §Contract). `agentId` here
// is the Shopify-subsystem URL slug (the Agent's `agent_id` field, NOT
// str(_id)) — the same value other Shopify endpoints take.
export type StylistSyncStatus =
  | 'idle'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed';
export type StylistSyncSchedule = 'weekly' | 'manual';

export interface StylistSyncState {
  status: StylistSyncStatus;
  product_count?: number;
  variant_count?: number;
  in_stock_count?: number;
  last_success_at?: string | null;
  schedule?: StylistSyncSchedule;
  estimated_minutes?: number;
  error?: string | null;
}

export interface StylistSyncStartResult {
  mode: 'live' | 'sync';
  started: boolean;
  status?: StylistSyncStatus;
  product_count?: number;
  variant_count?: number;
  in_stock_count?: number;
  estimated_minutes?: number;
}

// ---------------------------------------------------------------------------
// Prodagi Templates (files/templates/Plan-Templates.md)
//
// A Template is a Prodagi-curated bag of defaults: agent config + a pack of
// Knowledge Hub articles. Applying one is a SNAPSHOT — the created agent keeps
// no live link back to the template (Plan §1.1). Customers apply templates;
// they never author them, so there is no create/update/delete here.
//
// NOTE: `industry_templates` (agentsAPI.getIndustryTemplates) is a DIFFERENT,
// pre-existing thing — a thin {id,title,instruction} preset. Its `instruction`
// is now inert (Custom Instructions retired 2026-08-24); only id/title still
// drive the industry picker. The two are joined by `industry_key`.
// ---------------------------------------------------------------------------
export interface TemplateArticle {
  title: string;
  description?: string;
  order?: number;
}

export interface PromptTemplate {
  template_id: string;
  version: number;
  title: string;
  description: string;
  category: string;
  /** Ties back to an `industry_templates.id` — drives the AgentEditorModal toggle. */
  industry_key: string;
  is_active?: boolean;
  /** Short "who this agent becomes" blurb. Falls back to `description`. */
  persona_summary?: string;
  /** Bullet list of what the agent can do once applied. Optional. */
  capabilities?: string[];
  /** The article pack. The list endpoint may send titles only. */
  knowledge_articles?: TemplateArticle[];
  /** Cheaper alternatives the list endpoint may send instead of the full pack. */
  article_titles?: string[];
  article_count?: number;
}

export interface TemplateApplyResult {
  agent_id: string;
  agent_created: boolean;
  articles_created: number;
}

/** Article titles in a pack, whichever shape the server sent them in. */
export const templateArticleTitles = (tpl: PromptTemplate): string[] =>
  tpl.knowledge_articles?.length
    ? tpl.knowledge_articles.map((a) => a.title)
    : tpl.article_titles ?? [];

/** How many articles applying this template will create. */
export const templateArticleCount = (tpl: PromptTemplate): number =>
  tpl.article_count ?? templateArticleTitles(tpl).length;

export const templatesAPI = {
  list: () => api.get<{ templates: PromptTemplate[] }>('/api/templates'),

  // Apply is the single write primitive (Plan §1.2 — also the Orchestrator's).
  // Omit `agent_id` to have the server create a fresh agent from the template's
  // `agent_defaults` (the Templates-page path). Pass an existing `agent_id` to
  // seed only the knowledge pack onto an agent we just created (the
  // AgentEditorModal path).
  apply: (templateId: string, agentId?: string) =>
    api.post<TemplateApplyResult>(
      `/api/templates/${templateId}/apply`,
      agentId ? { agent_id: agentId } : {},
    ),
};

export const stylistAPI = {
  // Kick off (or no-op for small stores) a catalog sync.
  startSync: (agentId: string) =>
    api.post<StylistSyncStartResult>(`/api/stylist/sync/${agentId}`),

  // Poll current sync state — drives the progress dialog.
  getStatus: (agentId: string) =>
    api.get<StylistSyncState>(`/api/stylist/sync/${agentId}/status`),

  // Set weekly auto-sync vs manual-only.
  setSchedule: (agentId: string, schedule: StylistSyncSchedule) =>
    api.put<{ schedule: StylistSyncSchedule }>(
      `/api/stylist/sync/${agentId}/schedule`,
      { schedule },
    ),
};
